import axios from 'axios';
import { createApp } from 'vue';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import '@/styles/global-ui.css';
import '@/styles/swal-puestify.css';
import '@/styles/advanced-cropper.css';
import App from './App.vue';
import router from './router';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import { API_BASE_URL } from './config/api';
import { clearSession } from './utils/session';
import { initConnectivity, isOnline, isOfflineAllowedPath, onConnectivityChange } from './utils/connectivity';
import { hasFeature } from './utils/features';
import { cacheAppShellAssets, initAppShellCaching } from './utils/cacheAppShell';
import modalBackGuardMixin from './mixins/modalBackGuardMixin';
import { ensureOfflineCacheWarm, syncPendingOfflineWork } from './utils/posOffline/cache';
import { prefetchOfflineShellChunks } from './utils/prefetchOfflineShell';

let globalOfflineSyncInFlight = false;

async function triggerGlobalOfflineSync() {
    if (globalOfflineSyncInFlight) return;
    const usuario = localStorage.getItem('usuario');
    if (!usuario || !isOnline()) return;

    globalOfflineSyncInFlight = true;
    try {
        await syncPendingOfflineWork(usuario);
        await Promise.all([
            ensureOfflineCacheWarm(usuario),
            prefetchOfflineShellChunks(),
        ]);
    } finally {
        globalOfflineSyncInFlight = false;
    }
}

initConnectivity();
onConnectivityChange((online) => {
    if (online) {
        void cacheAppShellAssets();
        void triggerGlobalOfflineSync();
        return;
    }
    const path = router.currentRoute.value && router.currentRoute.value.path;
    if (!path || !path.startsWith('/u/')) return;
    if (isOfflineAllowedPath(path)) return;
    if (hasFeature('pos')) {
        router.push('/u/pos');
    }
});

axios.defaults.baseURL = API_BASE_URL;

axios.interceptors.request.use((config) => {
    try {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
    } catch (_) {
        /* ignore */
    }
    return config;
});

let manejandoSesionExpirada = false;

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const d = error.response && error.response.data;
        if (error.response && error.response.status === 403 && d && d.code === 'PLAN_SUSPENDIDO') {
            if (localStorage.getItem('rol') === 'vendedor') {
                clearSession();
                const onLogin = router.currentRoute.value && router.currentRoute.value.path === '/u/login';
                if (!onLogin) {
                    router.push({ path: '/u/login', query: { planVendedorSuspendido: '1' } });
                }
            }
        }

        if (!error.response && !isOnline()) {
            return Promise.reject(error);
        }

        if (error.response && error.response.status === 401) {
            const path = router.currentRoute.value && router.currentRoute.value.path;
            if (!isOnline() && path && isOfflineAllowedPath(path)) {
                return Promise.reject(error);
            }
            const hadToken = !!localStorage.getItem('token');
            const url = String((error.config && error.config.url) || '');
            const esLogin = url.includes('/login');
            if (hadToken && !esLogin && !manejandoSesionExpirada) {
                manejandoSesionExpirada = true;
                clearSession();
                const onLogin = router.currentRoute.value && router.currentRoute.value.path === '/u/login';
                if (!onLogin) {
                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'info',
                        title: 'Tu sesión expiró. Iniciá sesión de nuevo.',
                        showConfirmButton: false,
                        timer: 3500,
                    });
                    router.push('/u/login').finally(() => {
                        manejandoSesionExpirada = false;
                    });
                } else {
                    manejandoSesionExpirada = false;
                }
            }
        }

        return Promise.reject(error);
    }
);

Swal.mixin({
  buttonsStyling: false,
  confirmButtonText: 'Aceptar',
  cancelButtonText: 'Cancelar',
  /** La barra de tiempo en toasts suele verse como un “fondo” raro; desactivada por defecto. */
  timerProgressBar: false,
});

const app = createApp(App);
app.mixin(modalBackGuardMixin);
app.use(router);

app.use(VueGoogleMaps, {
    load: {
        key: process.env.VUE_APP_MAPS_KEY || '',
    },
});
if ('serviceWorker' in navigator) {
    navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((reg) => {
            if (reg.waiting) {
                reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
            reg.addEventListener('updatefound', () => {
                const worker = reg.installing;
                if (!worker) return;
                worker.addEventListener('statechange', () => {
                    if (worker.state === 'activated') {
                        void cacheAppShellAssets();
                    }
                });
            });
        })
        .catch((err) => {
            console.warn('[sw] No se pudo registrar el service worker:', err);
        });
}
app.mount('#app');
initAppShellCaching();
// Sesión ya abierta: precargar datos + chunks de rutas offline al arrancar.
(() => {
    const usuario = localStorage.getItem('usuario');
    const token = localStorage.getItem('token');
    if (!usuario || !token || !isOnline()) return;
    void Promise.all([
        ensureOfflineCacheWarm(usuario),
        prefetchOfflineShellChunks(),
    ]);
})();
